import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { companySelector } from "../../redux/modules/company/selectors";
import { register } from "../../redux/modules/auth/actions";
import Spinner from "../../components/Spinner";
import Popup from "../../components/Modal";
import { authSelector } from "../../redux/modules/auth/userSelector";
import { reset } from "../../redux/modules/auth/slice";
import { debounce } from "lodash";
import axios from "../../adapters/axios";
import Back from "../../components/Buttons/Back";
import TextInput from "../../components/TextInput";

export default function CreateUser() {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { status, message } = useSelector(authSelector);

  const [isUserNameAvailable, setIsUserNameAvailable] = useState(true);
  const [user, setUser] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [passwordVisibility, setPasswordVisibility] = useState(false);

  const handleSuccessModal = () => {
    dispatch(reset());
    navigate(-1);
  };

  const checkAvailability = async (val: string) => {
    if (!val || val.length < 4) return;

    const regex = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
    if (regex.test(val)) {
      setIsUserNameAvailable(false);
      return;
    }

    try {
      const res = await axios.post("checkAvailability", { user_id: val });
      setIsUserNameAvailable(res.data.available);
    } catch (err) {
      console.error(err);
      setIsUserNameAvailable(false);
    }
  };

  const handleDebouncedChange = useCallback(
    debounce((val) => checkAvailability(val), 500),
    []
  );

  useEffect(() => {
    if (status === "failed") {
      dispatch(reset());
    }
  }, [status]);

  const handleCreateUser = (e: any) => {
    e.preventDefault();

    if (!validate() || !isUserNameAvailable) return;

    const payload = { ...user };
    delete payload.confirmPassword;
    dispatch(register(payload));
  };

  const validate = () => {
    const tempErrors: any = {};
    let isValid = true;

    const requiredFields = ["firstName", "lastName", "email", "user_id", "companyName", "password", "confirmPassword"];
    requiredFields.forEach((field) => {
      if (!user[field]) {
        tempErrors[field] = ["This field is required"];
        isValid = false;
      }
    });

    const regex = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
    if (user.user_id && regex.test(user.user_id)) {
      tempErrors.user_id = ["User ID cannot contain special characters"];
      isValid = false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (user.email && !emailRegex.test(user.email)) {
      tempErrors.email = ["Invalid email format"];
      isValid = false;
    }

    const passRegex = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*?&#]).{10,}/;
    if (user.password && !passRegex.test(user.password)) {
      tempErrors.password = [
        "Password must contain at least one number, one uppercase, one lowercase, one special character, and be 10+ characters long",
      ];
      isValid = false;
    }

    if (user.password !== user.confirmPassword) {
      tempErrors.confirmPassword = ["Passwords do not match"];
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  return (
    <div className="page-content bg-white" id="content">
      {status === "loading" && <Spinner />}
      <Popup
        message={message}
        show={status === "success"}
        handleClose={handleSuccessModal}
      />

      <div className="row-header text-right">
        <div className="toggler p-2">
          <button className="btn nav-btn navbar-toggler d-block d-lg-none pull-right" type="button">
            <span className="fa fa-bars"></span>
          </button>
        </div>
      </div>

      <div className="user-box">
        <div className="container-fluid">
          <form onSubmit={handleCreateUser}>
            <div className="form-buttons">
              <Back />
              <button type="submit" className="btn suggest-btn">Create Profile</button>
            </div>

            {status === "failed" && (
              <span className="text-danger font-weight-bold">{message}</span>
            )}

            <TextInput
              label="First Name"
              attrs={{
                type: "text",
                id: "firstName",
                placeholder: "First Name",
                required: true,
              }}
              value={user.firstName}
              errors={errors.firstName}
              handleChange={(val: string) => setUser({ ...user, firstName: val })}
            />

            <TextInput
              label="Last Name"
              attrs={{
                type: "text",
                id: "lastName",
                placeholder: "Last Name",
                required: true,
              }}
              value={user.lastName}
              errors={errors.lastName}
              handleChange={(val: string) => setUser({ ...user, lastName: val })}
            />

<TextInput
  label="Email ID"
  attrs={{
    type: "email",
    id: "email",
    placeholder: "Your Email ID",
    required: true,
  }}
  value={user.email}
  errors={errors.email}
  handleChange={(val: string) => {
    setUser({ ...user, email: val });

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (val && !emailRegex.test(val)) {
      setErrors((prev: any) => ({ ...prev, email: ["Invalid email format"] }));
    } else {
      setErrors((prev: any) => ({ ...prev, email: [] }));
    }
  }}
/>


            <TextInput
              label="User ID"
              attrs={{
                type: "text",
                id: "user_id",
                placeholder: "Enter a Unique user ID",
                required: true,
                title: "No spaces or special characters. Minimum 4 characters.",
                minLength: 4,
              }}
              value={user.user_id}
              errors={errors.user_id}
              handleChange={(val: string) => {
                setUser({ ...user, user_id: val });
                handleDebouncedChange(val);
              }}
            />

            {!errors.user_id && user.user_id?.length >= 4 && (
              <p className={`font-weight-bold m-2 ${isUserNameAvailable ? "text-success" : "text-danger"}`}>
                {isUserNameAvailable ? "User Name Available :)" : "User Name Not Available :("}
              </p>
            )}

            <TextInput
              label="Company Name"
              attrs={{
                type: "text",
                id: "companyName",
                placeholder: "Company Name",
                required: true,
              }}
              value={user.companyName}
              errors={errors.companyName}
              handleChange={(val: string) => setUser({ ...user, companyName: val })}
            />

<div className="form-group">
  <label htmlFor="password">Password:</label>
  <div className="d-flex align-items-center">
    <input
      type={passwordVisibility ? "text" : "password"}
      className="form-control"
      id="password"
      placeholder="Enter Password"
      name="password"
      value={user.password || ""}
      minLength={10}
      onChange={(e) => {
        const val = e.target.value;
        setUser({ ...user, password: val });

        const passRegex = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*?&#]).{10,}/;
        
        if (val && !passRegex.test(val)) {
          setErrors((prev: any) => ({
            ...prev,
            password: [
              "Password must contain at least one number, one uppercase, one lowercase, one special character, and be 10+ characters long",
            ],
          }));
        } else {
          setErrors((prev: any) => ({ ...prev, password: [] }));
        }
      }}
      //pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{10,}$"
      pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#]).{10,}$"

      title="Must contain at least one number, one uppercase letter, one lowercase letter, one special character, and be 10+ characters"
      required
    />
    <span
      className="mx-4"
      title="Toggle Password"
      role="button"
      onClick={() => setPasswordVisibility((prev) => !prev)}
    >
      <i
        className={`fa ${passwordVisibility ? "fa-eye" : "fa-eye-slash"}`}
        style={{ color: passwordVisibility ? "green" : "black" }}
      ></i>
    </span>
  </div>
  {errors.password && <span className="text-danger">{errors.password[0]}</span>}
</div>


            <TextInput
              label="Confirm Password"
              attrs={{
                type: "password",
                id: "confirmPassword",
                placeholder: "Retype Password",
                required: true,
              }}
              value={user.confirmPassword}
              errors={errors.confirmPassword}
              handleChange={(val: string) => {
                setUser({ ...user, confirmPassword: val });
                if (user.password && val !== user.password) {
                  setErrors((prev: any) => ({
                    ...prev,
                    confirmPassword: ["Passwords do not match"],
                  }));
                } else {
                  setErrors((prev: any) => ({ ...prev, confirmPassword: [] }));
                }
              }}
              
            />
          </form>
        </div>
      </div>
    </div>
  );
}
